import {useEffect, useState} from 'react'
import TicketsService from '../../../../services/ticket.service'

type AssignedTo = 'tygo' | 'provider' | 'customer_service'

interface EscalateTicketProps {
  show: boolean
  ticketId: string | null
  onClose: () => void
  onSuccess: () => Promise<void> | void
}

const EscalateTicket = ({show, ticketId, onClose, onSuccess}: EscalateTicketProps) => {
  const [assignedTo, setAssignedTo] = useState<AssignedTo>('tygo')
  const [comment, setComment] = useState('')
  const [actors, setActors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [availableActors, setAvailableActors] = useState<string[]>([])
  const [loadingActors, setLoadingActors] = useState(false)

  const resetForm = () => {
    setAssignedTo('tygo')
    setComment('')
    setActors([])
  }

  // console.log('actors:', actors)

  const submit = async () => {
    if (!ticketId) return
    if (!comment.trim()) {
      alert('Comment is required')
      return
    }
    try {
      setSubmitting(true)
      await TicketsService.escalateTicket(ticketId, {assignedTo, comment, actors})
      resetForm()
      onClose()
      await onSuccess()
    } catch (error) {
      console.error(error)
      alert('Failed to escalate ticket')
    } finally {
      setSubmitting(false)
    }
  }

  const loadActors = async () => {
    try {
      setLoadingActors(true)
      const res = await TicketsService.getActors()
      setAvailableActors(res.data.actors || [])
    } catch (err) {
      console.error('Failed to load actors:', err)
    } finally {
      setLoadingActors(false)
    }
  }

  useEffect(() => {
    if (show) {
      loadActors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  if (!show) return null

  return (
    <>
      <div className='modal fade show d-block' tabIndex={-1} role='dialog'>
        <div className='modal-dialog modal-dialog-centered' role='document'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>Escalate Ticket</h5>
              <button
                type='button'
                className='btn-close'
                onClick={() => {
                  resetForm()
                  onClose()
                }}
                aria-label='Close'
              ></button>
            </div>
            <div className='modal-body'>
              <div className='mb-5'>
                <label className='form-label'>Escalate To</label>
                <select
                  className='form-select'
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value as AssignedTo)}
                >
                  <option value='tygo'>tygo</option>
                  <option value='provider'>provider</option>
                  <option value='customer_service'>customer_service</option>
                </select>
              </div>
              <div className='mb-5'>
                <label className='form-label'>
                  Comment<span className='text-danger'>*</span>
                </label>
                <textarea
                  className='form-control'
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder='Provide details for escalation'
                  rows={3}
                  required
                />
              </div>
              <div className='mb-5'>
                <label className='form-label'>Actors</label>
                {loadingActors ? (
                  <div className='text-muted'>Loading actors...</div>
                ) : (
                  <div className='d-flex flex-wrap gap-2'>
                    {availableActors.map((actor) => (
                      <button
                        key={actor}
                        type='button'
                        className={`btn btn-sm ${
                          actors.includes(actor)
                            ? 'btn-primary'
                            : 'btn-outline-secondary text-black'
                        }`}
                        onClick={() => {
                          if (actors.includes(actor)) {
                            setActors(actors.filter((a) => a !== actor))
                          } else {
                            setActors([...actors, actor])
                          }
                        }}
                      >
                        {actor}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className='modal-footer'>
              <button
                type='button'
                className='btn btn-light'
                onClick={() => {
                  resetForm()
                  onClose()
                }}
              >
                Cancel
              </button>
              <button
                type='button'
                className='btn btn-primary'
                onClick={submit}
                disabled={submitting || !comment.trim()}
              >
                {submitting ? 'Escalating...' : 'Escalate'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show'></div>
    </>
  )
}

export default EscalateTicket
